import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize, forkJoin, Observable, of, switchMap } from 'rxjs';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CrmAccount, CrmClient, PageResponse } from '../crm.model';
import { CrmService } from '../crm.service';
import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { Company } from '../../company/company.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { ToolbarService } from '../../services/toolbar.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from '../../shared/party-quick-create/party-quick-create-dialog.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';

interface QueuedClientAccountLogo {
  file: File;
  previewUrl: string;
  progress: number;
  state: 'queued' | 'uploading' | 'uploaded' | 'error';
}

@Component({
  selector: 'app-client-accounts-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    RippleModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent,
    PartyQuickCreateDialogComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './client-accounts-list.component.html',
  styleUrls: ['./client-accounts-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientAccountsListComponent implements OnInit, OnDestroy {
  private crmService = inject(CrmService);
  private customerService = inject(CustomerService);
  private sessionStorageService = inject(SessionStorageService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private fb = inject(FormBuilder);
  private firebaseStorageService = inject(FirebaseStorageService);

  accounts = signal<CrmAccount[]>([]);
  clients = signal<CrmClient[]>([]);
  kamCustomers = signal<Customer[]>([]);
  clientAccountManagerCustomers = signal<Customer[]>([]);
  accountManagerDirectory = signal<Customer[]>([]);
  loading = signal(false);
  saving = signal(false);
  accountDialog = signal(false);
  submitted = signal(false);
  showClientAccountContactDialog = signal(false);
  lastUpdated = signal<Date | null>(null);
  totalRecords = signal(0);
  selectedCrmClientId = signal<number | null>(null);
  queuedLogo = signal<QueuedClientAccountLogo | null>(null);

  readonly quickCreateOwningCompany: Company | null = this.sessionStorageService.getCompany() ?? this.toolbarService.customer()?.company ?? null;
  readonly quickCreateCountries = this.sessionStorageService.getCountryList() ?? [];
  readonly clientAccountContactCompany = computed<Company | null>(() => {
    const client = this.selectedCrmClient();
    if (!client?.linkedCompanyId) {
      return null;
    }
    return {
      id: client.linkedCompanyId,
      name: client.companyName,
      countryId: client.countryId || this.quickCreateOwningCompany?.countryId || 0
    } as Company;
  });

  searchText = signal('');
  crmClientFilter = signal<number | null>(null);
  categoryFilter = signal<string | null>(null);
  sourceFilter = signal<string | null>(null);

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/dashboard' },
    { label: 'Client Accounts', routerLink: '/crm/accounts' }
  ];

  accountForm = this.fb.group({
    id: [null as number | null],
    companyName: ['', Validators.required],
    logoUrl: [''],
    crmClientId: [null as number | null, Validators.required],
    category: [''],
    source: [''],
    kamCustomerId: [null as number | null],
    clientAccountManagerCustomerIds: [[] as number[]]
  });

  clientOptions = computed(() => this.clients().map(client => ({
    label: this.clientLabel(client),
    value: client.id!
  })));

  kamOptions = computed(() => this.kamCustomers().map(customer => ({
    label: customer.name,
    value: customer.id
  })));

  metrics = computed<PageMetric[]>(() => {
    const accounts = this.accounts();
    const linkedClients = new Set(accounts.map(account => account.crmClientId).filter(Boolean));
    const assignedKam = accounts.filter(account => !!account.kamCustomerId).length;
    const assignedManagers = new Set(accounts.flatMap(account => account.clientAccountManagerCustomerIds || []));
    return [
      { label: 'Client accounts', value: this.totalRecords() || accounts.length, tone: 'info' },
      { label: 'Linked CRM clients', value: linkedClients.size, tone: 'success' },
      { label: 'KAM assigned', value: assignedKam, tone: assignedKam ? 'success' : 'warn' },
      { label: 'Client account managers', value: assignedManagers.size, tone: assignedManagers.size ? 'success' : 'warn' }
    ];
  });

  ngOnInit(): void {
    this.loadReferenceData();
  }

  ngOnDestroy(): void {
    this.clearQueuedLogo();
  }

  get activeSearchCount(): number {
    return [this.searchText(), this.crmClientFilter(), this.categoryFilter(), this.sourceFilter()].filter(Boolean).length;
  }

  get lastUpdatedText(): string {
    return this.lastUpdated()
      ? `Updated ${this.lastUpdated()!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'Ready for account updates';
  }

  loadReferenceData(): void {
    const companyId = this.companyId();
    if (!companyId) return;

    this.loading.set(true);
    forkJoin({
      clients: this.crmService.searchClients({ companyId, size: 500, sortBy: 'companyName', sortDir: 'asc' }),
      customers: this.customerService.getCustomersByCompany(companyId)
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: ({ clients, customers }) => {
        this.clients.set(clients.content || []);
        this.kamCustomers.set(customers || []);
        this.loadAccounts();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Load Failed', detail: 'Could not load account setup data.' })
    });
  }

  loadAccounts(): void {
    const companyId = this.companyId();
    if (!companyId) return;

    this.loading.set(true);
    this.crmService.searchAccounts({
      companyId,
      crmClientId: this.crmClientFilter() || undefined,
      searchText: this.searchText(),
      category: this.categoryFilter() || undefined,
      source: this.sourceFilter() || undefined,
      page: 0,
      size: 500,
      sortBy: 'companyName',
      sortDir: 'asc'
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (response: PageResponse<CrmAccount>) => {
        this.accounts.set(response.content || []);
        this.totalRecords.set(response.totalElements || response.content?.length || 0);
        this.lastUpdated.set(new Date());
        this.loadAccountManagerDirectory(response.content || []);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Search Failed', detail: 'Could not load client accounts.' })
    });
  }

  applySearch(): void {
    this.loadAccounts();
  }

  clearSearch(): void {
    this.searchText.set('');
    this.crmClientFilter.set(null);
    this.categoryFilter.set(null);
    this.sourceFilter.set(null);
    this.loadAccounts();
  }

  openNew(): void {
    this.clearQueuedLogo();
    this.accountForm.reset({
      id: null,
      companyName: '',
      logoUrl: '',
      crmClientId: null,
      category: '',
      source: '',
      kamCustomerId: null,
      clientAccountManagerCustomerIds: []
    });
    this.selectedCrmClientId.set(null);
    this.clientAccountManagerCustomers.set([]);
    this.submitted.set(false);
    this.accountDialog.set(true);
  }

  editAccount(account: CrmAccount): void {
    this.clearQueuedLogo();
    this.accountForm.reset({
      id: account.id || null,
      companyName: account.companyName || '',
      logoUrl: account.logoUrl || '',
      crmClientId: account.crmClientId || null,
      category: account.category || '',
      source: account.source || '',
      kamCustomerId: account.kamCustomerId || null,
      clientAccountManagerCustomerIds: account.clientAccountManagerCustomerIds || []
    });
    this.selectedCrmClientId.set(account.crmClientId || null);
    this.loadClientAccountManagers(account.crmClientId);
    this.submitted.set(false);
    this.accountDialog.set(true);
  }

  hideDialog(): void {
    this.clearQueuedLogo();
    this.accountDialog.set(false);
    this.submitted.set(false);
  }

  saveAccount(): void {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    const companyId = this.companyId();
    if (!companyId || this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }

    const raw = this.accountForm.getRawValue();
    const payload: CrmAccount = {
      id: raw.id || undefined,
      companyName: raw.companyName?.trim() || '',
      logoUrl: raw.logoUrl?.trim() || '',
      crmClientId: raw.crmClientId || undefined,
      category: raw.category?.trim() || '',
      source: raw.source?.trim() || '',
      kamCustomerId: raw.kamCustomerId || undefined,
      clientAccountManagerCustomerIds: (raw.clientAccountManagerCustomerIds || []).filter((id): id is number => !!id),
      companyId
    };
    this.saving.set(true);
    this.uploadQueuedLogo().pipe(
      switchMap(uploadedLogoUrl => {
        if (uploadedLogoUrl) {
          payload.logoUrl = uploadedLogoUrl;
          this.accountForm.patchValue({ logoUrl: uploadedLogoUrl }, { emitEvent: false });
        }
        return payload.id ? this.crmService.updateAccount(payload) : this.crmService.addAccount(payload);
      }),
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: response => {
        if (response.success) {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Client account saved.' });
          this.hideDialog();
          this.loadAccounts();
        }
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Save Failed',
        detail: error?.error?.message || error?.message || 'Could not save client account.'
      })
    });
  }

  deleteAccount(account: CrmAccount): void {
    if (!account.id) return;
    this.confirmationService.confirm({
      header: 'Delete Client Account',
      message: `Delete <b>${account.companyName}</b>?`,
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.crmService.deleteAccount(account.id!).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Client account deleted.' });
            this.loadAccounts();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Could not delete client account.' })
        });
      }
    });
  }

  clientName(crmClientId?: number): string {
    return this.clients().find(client => client.id === crmClientId)?.companyName || '-';
  }

  kamName(kamCustomerId?: number): string {
    return this.kamCustomers().find(customer => customer.id === kamCustomerId)?.name || '-';
  }

  clientAccountManagerNames(account: CrmAccount): string[] {
    return (account.clientAccountManagerCustomerIds || []).map(managerId =>
      this.accountManagerDirectory().find(customer => customer.id === managerId)?.name || `Customer #${managerId}`
    );
  }

  onCrmClientChanged(crmClientId?: number | null): void {
    this.selectedCrmClientId.set(crmClientId || null);
    this.accountForm.patchValue({ clientAccountManagerCustomerIds: [] });
    this.loadClientAccountManagers(crmClientId);
  }

  startCreateClientAccountContact(): void {
    const client = this.selectedCrmClient();
    if (!client?.linkedCompanyId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'CRM client required',
        detail: 'Select a CRM client with a linked company before creating a client account contact.'
      });
      return;
    }
    this.showClientAccountContactDialog.set(true);
  }

  onClientAccountContactCreated(result: PartyQuickCreateResult): void {
    const customer = result.record as Customer;
    if (!customer?.id) {
      return;
    }
    this.clientAccountManagerCustomers.update(customers =>
      customers.some(existing => existing.id === customer.id) ? customers : [...customers, customer]
    );
    this.accountManagerDirectory.update(customers =>
      customers.some(existing => existing.id === customer.id) ? customers : [...customers, customer]
    );

    const managerIds = new Set(this.accountForm.get('clientAccountManagerCustomerIds')?.value || []);
    managerIds.add(customer.id);
    this.accountForm.patchValue({ clientAccountManagerCustomerIds: [...managerIds] });
    this.messageService.add({ severity: 'success', summary: 'Contact Added', detail: `${customer.name} was added to this client account.` });
  }

  removeClientAccountContact(customerId: number): void {
    const managerIds = (this.accountForm.get('clientAccountManagerCustomerIds')?.value || [])
      .filter(id => id !== customerId);
    this.accountForm.patchValue({ clientAccountManagerCustomerIds: managerIds });
  }

  dialogSummaryName(): string {
    return this.accountForm.get('companyName')?.value?.trim() || 'New Client Account';
  }

  dialogSummaryDescription(): string {
    return this.accountForm.get('id')?.value
      ? 'Update ownership and assignment details for this account without changing its CRM client linkage.'
      : 'Create an account-level commercial record linked to the right CRM client, owner, and client account managers.';
  }

  dialogClientLabel(): string {
    const crmClientId = this.accountForm.get('crmClientId')?.value;
    return crmClientId ? this.clientName(crmClientId) : 'Not linked';
  }

  dialogKamLabel(): string {
    const kamCustomerId = this.accountForm.get('kamCustomerId')?.value;
    return kamCustomerId ? this.kamName(kamCustomerId) : 'Unassigned';
  }

  dialogClientAccountManagerLabel(): string {
    const managerIds = this.accountForm.get('clientAccountManagerCustomerIds')?.value || [];
    if (!managerIds.length) {
      return 'Unassigned';
    }
    return managerIds.map(managerId =>
      this.clientAccountManagerCustomers().find(customer => customer.id === managerId)?.name || `Customer #${managerId}`
    ).join(', ');
  }

  clientAccountContactName(customerId: number): string {
    return this.clientAccountManagerCustomers().find(customer => customer.id === customerId)?.name
      || this.accountManagerDirectory().find(customer => customer.id === customerId)?.name
      || `Customer #${customerId}`;
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid File', detail: 'Please select an image file (PNG, JPG, SVG, etc.).' });
      input.value = '';
      return;
    }

    this.clearQueuedLogo();
    this.queuedLogo.set({
      file,
      previewUrl: URL.createObjectURL(file),
      progress: 0,
      state: 'queued'
    });
    input.value = '';
  }

  removeQueuedLogo(input?: HTMLInputElement): void {
    this.clearQueuedLogo();
    if (input) {
      input.value = '';
    }
  }

  removeExistingLogo(): void {
    this.accountForm.patchValue({ logoUrl: '' });
  }

  get currentLogoUrl(): string {
    return this.accountForm.get('logoUrl')?.value || '';
  }

  private clientLabel(client: CrmClient): string {
    return client.clientNumber ? `${client.clientNumber} - ${client.companyName}` : client.companyName;
  }

  private uploadQueuedLogo(): Observable<string | null> {
    const queued = this.queuedLogo();
    if (!queued) {
      return of(null);
    }

    this.queuedLogo.set({ ...queued, state: 'uploading' });
    return new Observable<string>(observer => {
      this.firebaseStorageService.uploadFileWithProgress(queued.file, 'crm/client-account-logos').subscribe({
        next: progress => {
          const current = this.queuedLogo();
          if (current) {
            this.queuedLogo.set({ ...current, progress: progress.progress });
          }
          if (progress.url) {
            if (current) {
              this.queuedLogo.set({ ...current, progress: 100, state: 'uploaded' });
            }
            observer.next(progress.url);
            observer.complete();
          }
        },
        error: error => {
          const current = this.queuedLogo();
          if (current) {
            this.queuedLogo.set({ ...current, state: 'error' });
          }
          observer.error(error);
        }
      });
    });
  }

  private clearQueuedLogo(): void {
    const previewUrl = this.queuedLogo()?.previewUrl;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    this.queuedLogo.set(null);
  }

  private loadClientAccountManagers(crmClientId?: number | null): void {
    const linkedCompanyId = this.clients().find(client => client.id === crmClientId)?.linkedCompanyId;
    if (!linkedCompanyId) {
      this.clientAccountManagerCustomers.set([]);
      return;
    }

    this.customerService.getCustomersByCompany(linkedCompanyId).subscribe({
      next: customers => this.clientAccountManagerCustomers.set(customers || []),
      error: () => {
        this.clientAccountManagerCustomers.set([]);
        this.messageService.add({ severity: 'warn', summary: 'Client contacts unavailable', detail: 'Could not load customers for the selected CRM client.' });
      }
    });
  }

  private selectedCrmClient(): CrmClient | undefined {
    return this.clients().find(client => client.id === this.selectedCrmClientId());
  }

  private loadAccountManagerDirectory(accounts: CrmAccount[]): void {
    const linkedCompanyIds = [...new Set(accounts
      .map(account => this.clients().find(client => client.id === account.crmClientId)?.linkedCompanyId)
      .filter((companyId): companyId is number => !!companyId))];
    if (!linkedCompanyIds.length) {
      this.accountManagerDirectory.set([]);
      return;
    }

    forkJoin(linkedCompanyIds.map(companyId => this.customerService.getCustomersByCompany(companyId))).subscribe({
      next: customerGroups => {
        const directory = new Map<number, Customer>();
        customerGroups.flat().forEach(customer => {
          if (customer.id) directory.set(customer.id, customer);
        });
        this.accountManagerDirectory.set([...directory.values()]);
      },
      error: () => this.accountManagerDirectory.set([])
    });
  }

  private companyId(): number | null {
    return this.toolbarService.customer()?.company?.id ?? null;
  }
}
